import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Textarea from '../../components/Common/Textarea';
import { showSuccess, showError } from '../../utils/swal';
import { Building2, Save, Setting, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';

const OfficeSettings = () => {
  const { user } = useAuth();
  const { canView } = usePermission();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingOffice, setSavingOffice] = useState(false);
  
  const [officeForm, setOfficeForm] = useState({
    name: '',
    phone: '',
    address: '',
    tax_number: '',
    tax_office: '',
  });

  const [settingsForm, setSettingsForm] = useState({
    data_visibility: 'private',
    default_portfolio_visibility: 'office',
    office_commission_rate: '',
  });

  useEffect(() => {
    if (!canView('settings')) {
      navigate('/');
      return;
    }

    fetchOfficeData();
  }, [user]);

  const fetchOfficeData = async () => {
    try {
      const [officeRes, settingsRes] = await Promise.all([
        api.get('/office'),
        api.get('/office/settings')
      ]);

      if (officeRes.data.success) {
        const off = officeRes.data.data;
        setOfficeForm({
          name: off.name || '',
          phone: off.phone || '',
          address: off.address || '',
          tax_number: off.tax_number || '',
          tax_office: off.tax_office || '',
        });
      }

      if (settingsRes.data.success) {
        const set = settingsRes.data.data;
        setSettingsForm({
          data_visibility: set.data_visibility || 'private',
          default_portfolio_visibility: set.default_portfolio_visibility || 'office',
          office_commission_rate: set.office_commission_rate || '',
        });
      }
    } catch (error) {
      console.error('Ofis verileri yüklenemedi:', error);
      showError('Ofis verileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOfficeSave = async (e) => {
    e.preventDefault();
    setSavingOffice(true);
    try {
      await api.put('/office', officeForm);
      showSuccess('Ofis bilgileri başarıyla güncellendi');
    } catch (error) {
      console.error('Ofis güncelleme hatası', error);
      showError(error.response?.data?.message || 'Güncellenirken bir hata oluştu');
    } finally {
      setSavingOffice(false);
    }
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put('/office/settings', settingsForm);
      showSuccess('Ofis ayarları başarıyla güncellendi');
    } catch (error) {
      console.error('Ayarlar güncelleme hatası', error);
      showError(error.response?.data?.message || 'Ayarlar güncellenirken bir hata oluştu');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <Card 
        title={<div className="flex items-center gap-2"><Building2 className="text-primary-600" size={24} /> Ofis Bilgileri</div>}
      >
        <form onSubmit={handleOfficeSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input 
            label="Ofis Adı" 
            value={officeForm.name} 
            onChange={(e) => setOfficeForm({...officeForm, name: e.target.value})} 
            required
          />
          <Input 
            label="Telefon Numaramız" 
            value={officeForm.phone} 
            onChange={(e) => setOfficeForm({...officeForm, phone: e.target.value})} 
          />
          <Input 
            label="Vergi Dairesi" 
            value={officeForm.tax_office} 
            onChange={(e) => setOfficeForm({...officeForm, tax_office: e.target.value})} 
          />
          <Input 
            label="Vergi Numarası" 
            value={officeForm.tax_number} 
            onChange={(e) => setOfficeForm({...officeForm, tax_number: e.target.value})} 
          />
          <div className="md:col-span-2">
            <Textarea 
              label="Adres" 
              value={officeForm.address} 
              onChange={(e) => setOfficeForm({...officeForm, address: e.target.value})} 
              rows={3}
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
             <Button type="submit" disabled={savingOffice}>
                <Save size={18} className="mr-2" />
                {savingOffice ? 'Kaydediliyor...' : 'Kaydet'}
             </Button>
          </div>
        </form>
      </Card>

      <Card 
        title={<div className="flex items-center gap-2"><ShieldCheck className="text-primary-600" size={24} /> Veri Görünürlük Ayarları</div>}
      >
        <form onSubmit={handleSettingsSave} className="space-y-6">
          <div className="bg-gray-50 p-4 border border-gray-100 rounded-lg">
             <Select 
               label="Müşteri & Görüşme Veri Paylaşımı"
               value={settingsForm.data_visibility}
               onChange={e => setSettingsForm({...settingsForm, data_visibility: e.target.value})}
               options={[
                 {value: 'private', label: 'Sadece Kendi Verilerini Görsün (Gizli)'},
                 {value: 'shared', label: 'Tüm Ofis Verileri Şeffaf Olarak Görünsün'}
               ]}
             />
             <p className="text-sm text-gray-500 mt-2">
               Eğer "Şeffaf" seçilirse danışmanlar birbirlerinin kayıtlı müşterilerini veya aktivitelerini görüntüleyebilir. Yönetici konumundaki kullanıcılar her zaman her kaydı görüntüleme iznine sahiptir.
             </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-100 rounded-lg">
             <Select 
               label="Portföy Varsayılan Görünürlüğü"
               value={settingsForm.default_portfolio_visibility}
               onChange={e => setSettingsForm({...settingsForm, default_portfolio_visibility: e.target.value})}
               options={[
                 {value: 'office', label: 'Tüm Ofise Açık'},
                 {value: 'owner', label: 'Sadece Sahibine Özel'}
               ]}
             />
             <p className="text-sm text-gray-500 mt-2">
               Bu ayar yeni eklenen portföylerde baz alınacaktır. Danışmanlar isterlerse portföylerini diğer danışmanlara spesifik olarak yetkilendirebilirler.
             </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-100 rounded-lg">
             <Input 
               label="Ofis Komisyon Payı (%)"
               type="number"
               step="0.01"
               min="0"
               max="100"
               value={settingsForm.office_commission_rate}
               onChange={e => setSettingsForm({...settingsForm, office_commission_rate: e.target.value})}
               placeholder="Örn: 50.00"
             />
             <p className="text-sm text-gray-500 mt-2">
               Danışmanların gerçekleştirdiği işlemlerden ofisin alacağı pay oranı. Finansal hesaplamalarda varsayılan olarak bu oran kullanılacaktır.
             </p>
          </div>

          <div className="flex justify-end">
             <Button type="submit" disabled={savingSettings}>
                <Save size={18} className="mr-2" />
                {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
             </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default OfficeSettings;
